import React, { Component } from 'react';
import PropTypes from 'prop-types';

class CustomRecaptcha extends Component {
  static propTypes = {
    sitekey: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    size: PropTypes.oneOf(['compact', 'normal', 'invisible']),
    badge: PropTypes.oneOf(['bottomright', 'bottomleft', 'inline']),
    theme: PropTypes.oneOf(['light', 'dark']),
    tabindex: PropTypes.number,
    onExpired: PropTypes.func,
    onError: PropTypes.func,
    onLoad: PropTypes.func,
  };

  static defaultProps = {
    onChange: () => {},
    size: 'normal',
    badge: 'bottomright',
    theme: 'light',
    tabindex: 0,
    onExpired: () => {},
    onError: () => {},
    onLoad: () => {},
  };

  constructor(props) {
    super(props);
    this.recaptchaRef = React.createRef();
    this.widgetId = null;
    this.scriptLoaded = false;
  }

  componentDidMount() {
    this.loadRecaptchaScript();
  }

  componentWillUnmount() {
    if (this.widgetId !== null && window.grecaptcha) {
      try {
        window.grecaptcha.reset(this.widgetId);
      } catch (e) {
        // Ignore cleanup errors
      }
    }
  }

  loadRecaptchaScript = () => {
    // Check if script is already loaded
    if (window.grecaptcha) {
      this.renderRecaptcha();
      return;
    }

    // Check if script tag already exists
    if (document.querySelector('script[src*="recaptcha"]')) {
      // Wait for it to load
      const checkLoaded = () => {
        if (window.grecaptcha) {
          this.renderRecaptcha();
        } else {
          setTimeout(checkLoaded, 100);
        }
      };
      checkLoaded();
      return;
    }

    // Create and load script
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
    script.async = true;
    script.defer = true;

    // Set global callback
    window.onRecaptchaLoad = () => {
      this.scriptLoaded = true;
      this.renderRecaptcha();
    };

    document.head.appendChild(script);
  };

  renderRecaptcha = () => {
    if (!window.grecaptcha || !this.recaptchaRef.current || this.widgetId !== null) {
      return;
    }

    try {
      this.widgetId = window.grecaptcha.render(this.recaptchaRef.current, {
        sitekey: this.props.sitekey,
        callback: this.handleChange,
        'expired-callback': this.props.onExpired,
        'error-callback': this.props.onError,
        size: this.props.size,
        badge: this.props.badge,
        theme: this.props.theme,
        tabindex: this.props.tabindex,
      });

      this.props.onLoad();
    } catch (error) {
      console.error('reCAPTCHA render error:', error);
      this.props.onError(error);
    }
  };

  handleChange = (token) => {
    this.props.onChange(token);
  };

  reset = () => {
    if (this.widgetId !== null && window.grecaptcha) {
      try {
        window.grecaptcha.reset(this.widgetId);
      } catch (error) {
        console.error('reCAPTCHA reset error:', error);
      }
    }
  };

  execute = () => {
    if (this.widgetId !== null && window.grecaptcha) {
      try {
        return window.grecaptcha.execute(this.widgetId);
      } catch (error) {
        console.error('reCAPTCHA execute error:', error);
        return Promise.reject(error);
      }
    }
    return Promise.reject(new Error('reCAPTCHA not ready'));
  };

  getResponse = () => {
    if (this.widgetId !== null && window.grecaptcha) {
      try {
        return window.grecaptcha.getResponse(this.widgetId);
      } catch (error) {
        console.error('reCAPTCHA getResponse error:', error);
        return '';
      }
    }
    return '';
  };

  render() {
    return <div ref={this.recaptchaRef} />;
  }
}

export default CustomRecaptcha;